import { api } from './apiClient';

/** Full product-master row (snake_case, as returned by the API). */
export interface Product {
  id: string;
  product_code: string;
  product_name: string;
  product_type?: string;
  description?: string;
  short_description?: string;
  hsn_code?: string;
  sac_code?: string;
  unit_of_measure: string;
  uom_id?: number;
  purchase_uom_id?: number;
  sales_uom_id?: number;
  stock_uom_id?: number;
  category?: string;
  category_id?: number;
  category_name?: string;
  brand?: string;
  manufacturer?: string;
  model?: string;
  sku?: string;
  barcode?: string;
  mpn?: string;
  base_price: number;
  purchase_price: number;
  gst_rate: number;
  tax_code_id?: number;
  tax_name?: string;
  tax_code_rate?: number;
  is_tax_inclusive?: number | boolean;
  reverse_charge?: number | boolean;
  // capability flags
  can_purchase?: number | boolean;
  can_sell?: number | boolean;
  can_stock?: number | boolean;
  can_manufacture?: number | boolean;
  can_consume?: number | boolean;
  can_capitalize?: number | boolean;
  // inventory control
  is_stock_item?: number | boolean;
  is_batch_tracked?: number | boolean;
  is_serial_tracked?: number | boolean;
  shelf_life_days?: number;
  safety_stock?: number;
  lead_time_days?: number;
  reorder_level?: number;
  min_stock?: number;
  max_stock?: number;
  eoq?: number;
  preferred_vendor_id?: number;
  // accounting overrides
  inventory_account_id?: number;
  purchase_account_id?: number;
  sales_account_id?: number;
  cogs_account_id?: number;
  expense_account_id?: number;
  consumption_account_id?: number;
  // qc / production
  qc_required?: number | boolean;
  incoming_inspection?: number | boolean;
  process_qc?: number | boolean;
  final_qc?: number | boolean;
  make_or_buy?: string;
  asset_category_id?: number;
  material_type?: string;
  item_status?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/** camelCase create/update payload (what the API's model consumes). */
export interface ProductInput {
  productCode?: string;
  productName: string;
  productType?: string;
  description?: string;
  shortDescription?: string;
  hsnCode?: string;
  sacCode?: string;
  unitOfMeasure?: string;
  uomId?: number;
  purchaseUomId?: number;
  salesUomId?: number;
  stockUomId?: number;
  category?: string;
  categoryId?: number;
  brand?: string;
  manufacturer?: string;
  model?: string;
  sku?: string;
  barcode?: string;
  mpn?: string;
  basePrice?: number;
  purchasePrice?: number;
  gstRate?: number;
  taxCodeId?: number;
  isTaxInclusive?: boolean;
  reverseCharge?: boolean;
  canPurchase?: boolean;
  canSell?: boolean;
  canStock?: boolean;
  canManufacture?: boolean;
  canConsume?: boolean;
  canCapitalize?: boolean;
  isStockItem?: boolean;
  isBatchTracked?: boolean;
  isSerialTracked?: boolean;
  shelfLifeDays?: number;
  safetyStock?: number;
  leadTimeDays?: number;
  reorderLevel?: number;
  minStock?: number;
  maxStock?: number;
  eoq?: number;
  preferredVendorId?: number;
  inventoryAccountId?: number;
  purchaseAccountId?: number;
  salesAccountId?: number;
  cogsAccountId?: number;
  expenseAccountId?: number;
  consumptionAccountId?: number;
  qcRequired?: boolean;
  incomingInspection?: boolean;
  processQc?: boolean;
  finalQc?: boolean;
  makeOrBuy?: string;
  assetCategoryId?: number;
  isActive?: boolean;
  itemStatus?: string;
}

export interface ProductTypeMeta {
  key: string;
  label: string;
  stockable: boolean;
  defaultItemType: string;
  prefix: string;
  capabilities: Record<string, number>;
}
export interface ProductPolicy {
  itemTypes: string[];
  productTypes: ProductTypeMeta[];
  itemTypeAllowedProductTypes: Record<string, string[]>;
  compatibilityMatrix: Array<{ productType: string; label: string; purchase: boolean; sales: boolean; stock: boolean; production: boolean; asset: boolean }>;
}

export interface ProductVendor {
  id: number; vendor_id: number; vendor_name?: string; vendor_code?: string;
  vendor_part_no?: string; unit_price?: number; currency?: string;
  lead_time_days?: number; moq?: number; is_preferred?: number | boolean; last_purchase_price?: number;
}
export interface ProductWarehouseSetting {
  id: number; warehouse_id: number; warehouse_name?: string; warehouse_code?: string;
  min_stock?: number; max_stock?: number; reorder_level?: number; default_bin_id?: number;
}

interface ProductsResponse { products: Product[]; count: number; }
interface ProductResponse { product: Product; }
interface CategoriesResponse { categories: string[]; }

export interface ProductFilters {
  isActive?: boolean;
  status?: string;
  category?: string;
  categoryId?: number;
  productType?: string;
  forItemType?: string;
  stockable?: boolean;
  purchasable?: boolean;
  saleable?: boolean;
  hsnCode?: string;
  search?: string;
}

/** Accept either a camelCase ProductInput or a legacy snake-case Partial<Product>
 *  and always emit the camelCase payload the API expects. camelCase keys win. */
const toPayload = (p: any): Record<string, any> => ({
  productName: p.productName ?? p.product_name,
  productType: p.productType ?? p.product_type,
  productCode: p.productCode ?? p.product_code,
  description: p.description,
  shortDescription: p.shortDescription ?? p.short_description,
  hsnCode: p.hsnCode ?? p.hsn_code,
  sacCode: p.sacCode ?? p.sac_code,
  unitOfMeasure: p.unitOfMeasure ?? p.unit_of_measure,
  uomId: p.uomId ?? p.uom_id,
  purchaseUomId: p.purchaseUomId ?? p.purchase_uom_id,
  salesUomId: p.salesUomId ?? p.sales_uom_id,
  stockUomId: p.stockUomId ?? p.stock_uom_id,
  category: p.category,
  categoryId: p.categoryId ?? p.category_id,
  brand: p.brand, manufacturer: p.manufacturer, model: p.model,
  sku: p.sku, barcode: p.barcode, mpn: p.mpn,
  basePrice: p.basePrice ?? p.base_price,
  purchasePrice: p.purchasePrice ?? p.purchase_price,
  gstRate: p.gstRate ?? p.gst_rate,
  taxCodeId: p.taxCodeId ?? p.tax_code_id,
  isTaxInclusive: p.isTaxInclusive ?? p.is_tax_inclusive,
  reverseCharge: p.reverseCharge ?? p.reverse_charge,
  canPurchase: p.canPurchase, canSell: p.canSell, canStock: p.canStock,
  canManufacture: p.canManufacture, canConsume: p.canConsume, canCapitalize: p.canCapitalize,
  isStockItem: p.isStockItem ?? p.is_stock_item,
  isBatchTracked: p.isBatchTracked ?? p.is_batch_tracked,
  isSerialTracked: p.isSerialTracked ?? p.is_serial_tracked,
  shelfLifeDays: p.shelfLifeDays ?? p.shelf_life_days,
  safetyStock: p.safetyStock ?? p.safety_stock,
  leadTimeDays: p.leadTimeDays ?? p.lead_time_days,
  reorderLevel: p.reorderLevel ?? p.reorder_level,
  minStock: p.minStock ?? p.min_stock,
  maxStock: p.maxStock ?? p.max_stock,
  eoq: p.eoq,
  preferredVendorId: p.preferredVendorId ?? p.preferred_vendor_id,
  inventoryAccountId: p.inventoryAccountId ?? p.inventory_account_id,
  purchaseAccountId: p.purchaseAccountId ?? p.purchase_account_id,
  salesAccountId: p.salesAccountId ?? p.sales_account_id,
  cogsAccountId: p.cogsAccountId ?? p.cogs_account_id,
  expenseAccountId: p.expenseAccountId ?? p.expense_account_id,
  consumptionAccountId: p.consumptionAccountId ?? p.consumption_account_id,
  qcRequired: p.qcRequired ?? p.qc_required,
  incomingInspection: p.incomingInspection ?? p.incoming_inspection,
  makeOrBuy: p.makeOrBuy ?? p.make_or_buy,
  assetCategoryId: p.assetCategoryId ?? p.asset_category_id,
  itemStatus: p.itemStatus ?? p.item_status,
  isActive: p.isActive ?? p.is_active,
});

export const productService = {
  getProducts: async (params?: ProductFilters): Promise<ProductsResponse> =>
    api.get<ProductsResponse>('/products', { params }),

  getProduct: async (id: string): Promise<ProductResponse> =>
    api.get<ProductResponse>(`/products/${id}`),

  createProduct: async (product: ProductInput | Partial<Product>): Promise<ProductResponse> =>
    api.post<ProductResponse>('/products', toPayload(product)),

  updateProduct: async (id: string, product: ProductInput | Partial<Product>): Promise<ProductResponse> =>
    api.put<ProductResponse>(`/products/${id}`, toPayload(product)),

  toggleStatus: async (id: string): Promise<ProductResponse> =>
    api.patch<ProductResponse>(`/products/${id}/toggle-status`),

  bulkStatus: async (ids: Array<number | string>, active: boolean): Promise<{ updated: number }> =>
    api.post('/products/bulk-status', { ids, active }),

  getCategories: async (): Promise<CategoriesResponse> =>
    api.get<CategoriesResponse>('/products/categories'),

  searchProducts: async (term: string, forItemType?: string): Promise<ProductsResponse> =>
    api.get<ProductsResponse>('/products/search', { params: { q: term, forItemType } }),

  getPolicy: async (): Promise<ProductPolicy> =>
    api.get<ProductPolicy>('/products/policy'),

  // product ↔ vendor map
  getVendors: async (id: string): Promise<{ vendors: ProductVendor[] }> =>
    api.get(`/products/${id}/vendors`),
  addVendor: async (id: string, body: any): Promise<{ id: number }> =>
    api.post(`/products/${id}/vendors`, body),
  removeVendor: async (id: string, vendorRowId: number): Promise<any> =>
    api.delete(`/products/${id}/vendors/${vendorRowId}`),

  // per-warehouse settings
  getWarehouseSettings: async (id: string): Promise<{ settings: ProductWarehouseSetting[] }> =>
    api.get(`/products/${id}/warehouses`),
  upsertWarehouseSetting: async (id: string, body: any): Promise<any> =>
    api.post(`/products/${id}/warehouses`, body),
};
